import React, { useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import {
  Eye, FileText, Table2, PenTool, Shield, Sparkles, Loader2,
  Languages, CheckCircle, AlertTriangle, Download, Upload, RefreshCw,
  Camera, X as XIcon, SwitchCamera
} from 'lucide-react';
import { useCreditGate } from '../CreditGate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// FastAPI `detail` may be a string OR an object (e.g. a 402 quota payload).
// Always resolve to a human-readable string so toasts never show "[object Object]".
const resolveDetailMessage = (errData, fallback) => {
  const d = errData?.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (d && typeof d === 'object') return d.message || d.error || fallback;
  if (typeof errData?.message === 'string' && errData.message.trim()) return errData.message;
  return fallback;
};

const VisionInsightsPanel = ({ documentId, headers, onOcrComplete }) => {
  const { requestRun, dialog } = useCreditGate('vision_analysis', 'Confirm Vision Analysis');
  const [loading, setLoading] = useState(false);
  const [activeStage, setActiveStage] = useState(null);
  const [results, setResults] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [reconstructFormat, setReconstructFormat] = useState('json');
  const [inputMode, setInputMode] = useState('upload'); // 'upload' | 'scan'
  const [scannerActive, setScannerActive] = useState(false);
  const [cameraPreview, setCameraPreview] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState('environment');

  const token = localStorage.getItem('reasondms_token');
  const authHeaders = { 'Authorization': `Bearer ${token}` };

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setResults(null);
      setCameraPreview(null);
    }
  }, []);

  const startScanner = async () => {
    try {
      const constraints = { video: { facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setScannerActive(true);
      setCameraPreview(null);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Camera permission denied. Please allow camera access.');
      } else if (err.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else {
        toast.error('Unable to access camera: ' + err.message);
      }
    }
  };

  const stopScanner = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setScannerActive(false);
  };

  const switchCamera = async () => {
    stopScanner();
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newMode);
    setTimeout(() => startScanner(), 200);
  };

  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `scan_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setSelectedFile(file);
        setResults(null);
        const previewUrl = URL.createObjectURL(blob);
        setCameraPreview(previewUrl);
        stopScanner();
        toast.success('Document captured! Ready for processing.');
      }
    }, 'image/jpeg', 0.92);
  };

  const retakeScan = () => {
    setCameraPreview(null);
    setSelectedFile(null);
    startScanner();
  };

  const runFullPipeline = async () => {
    if (!selectedFile) {
      toast.error('Please select a document image first');
      return;
    }
    setLoading(true);
    setActiveStage('Processing...');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('output_format', 'json');

      const response = await fetch(`${API_URL}/api/vision/process`, {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });
      if (!response.ok) {
        let errorMsg = 'Processing failed';
        try {
          const errData = await response.json();
          errorMsg = resolveDetailMessage(errData, errorMsg);
        } catch { errorMsg = response.status === 404 ? 'Vision AI service not available. Please check server configuration.' : `Server error (${response.status})`; }
        throw new Error(errorMsg);
      }
      const data = await response.json();
      setResults(data);
      if (data.ocr) onOcrComplete?.(data.ocr);
      toast.success(`Vision AI processed ${data.total_stages} stages successfully!`);
    } catch (err) {
      toast.error(err.message || 'Vision processing failed');
    } finally {
      setLoading(false);
      setActiveStage(null);
    }
  };

  const runSingleStage = async (endpoint, label) => {
    if (!selectedFile) {
      toast.error('Please select a document image first');
      return;
    }
    setLoading(true);
    setActiveStage(label);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_URL}/api/vision/${endpoint}`, {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });
      if (!response.ok) {
        let errorMsg = `${label} failed`;
        try {
          const errData = await response.json();
          errorMsg = resolveDetailMessage(errData, errorMsg);
        } catch { errorMsg = response.status === 404 ? 'Vision AI service not available. Please check server configuration.' : `Server error (${response.status})`; }
        throw new Error(errorMsg);
      }
      const data = await response.json();
      setResults(prev => ({ ...(prev || {}), [endpoint.replace('-', '_').replace('/', '_')]: data }));
      if (endpoint === 'ocr' && data.success) onOcrComplete?.(data);
      toast.success(`${label} complete!`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
      setActiveStage(null);
    }
  };

  const reconstructAndExport = async () => {
    if (!selectedFile) {
      toast.error('Please select a document image first');
      return;
    }
    setLoading(true);
    setActiveStage('Reconstruction');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('output_format', reconstructFormat);

      const response = await fetch(`${API_URL}/api/vision/document-reconstruct`, {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });
      if (!response.ok) {
        let errorMsg = 'Reconstruction failed';
        try { errorMsg = resolveDetailMessage(await response.json(), errorMsg); } catch { errorMsg = `Server error (${response.status})`; }
        throw new Error(errorMsg);
      }

      const ct = response.headers.get('content-type') || '';
      // Only treat the payload as binary when the server actually streamed a binary
      // file. On a graceful AI failure the backend returns 200 + JSON {success:false},
      // so relying on the requested format alone would download a corrupted file.
      const isBinary = ct.includes('application/pdf') || ct.includes('officedocument');

      if (isBinary) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        const baseName = (selectedFile.name || 'reconstructed').replace(/\.[^/.]+$/, '');
        a.download = `${baseName}_reconstructed.${reconstructFormat}`;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`Downloaded ${reconstructFormat.toUpperCase()}`);
      } else {
        // Text formats (JSON / Markdown / HTML / CSV): build a UTF-8 text blob and
        // trigger a download so non-English (Unicode) content is preserved verbatim.
        const data = await response.json();
        if (data.success === false) {
          throw new Error(data.error || 'Reconstruction failed');
        }
        const FORMAT_META = {
          json: { mime: 'application/json', ext: 'json' },
          markdown: { mime: 'text/markdown', ext: 'md' },
          html: { mime: 'text/html', ext: 'html' },
          csv: { mime: 'text/csv', ext: 'csv' },
        };
        const meta = FORMAT_META[reconstructFormat] || FORMAT_META.json;
        const textContent = reconstructFormat === 'json'
          ? JSON.stringify(data, null, 2)
          : (data.document ?? '');
        const blob = new Blob([textContent], { type: `${meta.mime};charset=utf-8` });
        const url = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        const baseName = (selectedFile.name || 'reconstructed').replace(/\.[^/.]+$/, '');
        a.download = `${baseName}_reconstructed.${meta.ext}`;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        setResults((prev) => ({ ...(prev || {}), reconstruction: data }));
        toast.success(`Downloaded ${reconstructFormat.toUpperCase()}`);
      }
    } catch (err) {
      toast.error(err.message || 'Reconstruction failed');
    } finally {
      setLoading(false);
      setActiveStage(null);
    }
  };

  const ConfidenceBadge = ({ score }) => {
    const pct = Math.round((score || 0) * 100);
    const color = pct >= 90 ? 'bg-emerald-100 text-emerald-700' :
      pct >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>{pct}%</span>;
  };

  return (
    <Card data-testid="vision-insights-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-indigo-600" />
            <CardTitle className="text-lg text-indigo-900">Vision Intelligence</CardTitle>
          </div>
          <Badge variant="outline" className="text-xs">Engine v1.0</Badge>
        </div>
        <CardDescription className="text-xs">Advanced OCR, Table Extraction, Signatures, Classification & more</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Input Mode Selector */}
        <div className="flex items-center gap-2 mb-1">
          <Button
            data-testid="vision-upload-mode-btn"
            variant={inputMode === 'upload' ? 'default' : 'outline'}
            size="sm"
            className={`text-xs ${inputMode === 'upload' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
            onClick={() => { setInputMode('upload'); stopScanner(); }}
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload
          </Button>
          <Button
            data-testid="vision-scan-mode-btn"
            variant={inputMode === 'scan' ? 'default' : 'outline'}
            size="sm"
            className={`text-xs ${inputMode === 'scan' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
            onClick={() => { setInputMode('scan'); }}
          >
            <Camera className="h-3.5 w-3.5 mr-1.5" /> Scan Document
          </Button>
        </div>

        {/* Upload Mode */}
        {inputMode === 'upload' && (
          <div className="flex items-center gap-3">
            <label className="flex-1">
              <div className="flex items-center gap-2 border-2 border-dashed border-slate-200 rounded-lg p-3 cursor-pointer hover:border-indigo-300 transition-colors">
                <Upload className="h-4 w-4 text-slate-400" />
                <span className="text-sm text-slate-500">
                  {selectedFile ? selectedFile.name : 'Select document image...'}
                </span>
              </div>
              <input
                data-testid="vision-file-input"
                type="file"
                accept="image/*,.pdf"
                className="hidden"
                onChange={handleFileSelect}
              />
            </label>
            <Button
              data-testid="vision-full-pipeline-btn"
              onClick={() => requestRun(runFullPipeline)}
              disabled={!selectedFile || loading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Full Analysis
            </Button>
          </div>
        )}

        {/* Scan Mode */}
        {inputMode === 'scan' && (
          <div className="space-y-3">
            {!scannerActive && !cameraPreview && (
              <div
                data-testid="vision-scan-area"
                className="border-2 border-dashed border-indigo-200 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-all"
                onClick={startScanner}
              >
                <Camera className="h-8 w-8 text-indigo-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Click to open camera</p>
                <p className="text-xs text-slate-400 mt-1">Position your document in frame and capture</p>
              </div>
            )}

            {scannerActive && (
              <div className="relative rounded-lg overflow-hidden bg-black">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full max-h-[350px] object-contain"
                />
                {/* Scanner overlay guides */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-4 border-2 border-white/40 rounded-lg" />
                  <div className="absolute top-4 left-4 w-8 h-8 border-t-3 border-l-3 border-indigo-400 rounded-tl-lg" />
                  <div className="absolute top-4 right-4 w-8 h-8 border-t-3 border-r-3 border-indigo-400 rounded-tr-lg" />
                  <div className="absolute bottom-16 left-4 w-8 h-8 border-b-3 border-l-3 border-indigo-400 rounded-bl-lg" />
                  <div className="absolute bottom-16 right-4 w-8 h-8 border-b-3 border-r-3 border-indigo-400 rounded-br-lg" />
                </div>
                {/* Camera controls */}
                <div className="absolute bottom-0 inset-x-0 flex items-center justify-center gap-4 py-3 bg-gradient-to-t from-black/60 to-transparent">
                  <Button
                    data-testid="vision-switch-camera-btn"
                    variant="ghost"
                    size="sm"
                    onClick={switchCamera}
                    className="text-white hover:bg-white/20 text-xs"
                  >
                    <SwitchCamera className="h-4 w-4 mr-1" /> Flip
                  </Button>
                  <Button
                    data-testid="vision-capture-btn"
                    onClick={captureImage}
                    className="bg-indigo-600 hover:bg-indigo-700 rounded-full w-12 h-12 p-0"
                  >
                    <Camera className="h-5 w-5" />
                  </Button>
                  <Button
                    data-testid="vision-close-scanner-btn"
                    variant="ghost"
                    size="sm"
                    onClick={stopScanner}
                    className="text-white hover:bg-white/20 text-xs"
                  >
                    <XIcon className="h-4 w-4 mr-1" /> Close
                  </Button>
                </div>
                <canvas ref={canvasRef} className="hidden" />
              </div>
            )}

            {cameraPreview && (
              <div className="space-y-2">
                <div className="relative rounded-lg overflow-hidden border border-indigo-200">
                  <img src={cameraPreview} alt="Captured document" className="w-full max-h-[300px] object-contain bg-slate-50" />
                  <Badge className="absolute top-2 right-2 bg-emerald-500 text-white border-0 text-xs">Captured</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="vision-retake-btn"
                    variant="outline"
                    size="sm"
                    onClick={retakeScan}
                    className="text-xs"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retake
                  </Button>
                  <Button
                    data-testid="vision-scan-pipeline-btn"
                    onClick={() => requestRun(runFullPipeline)}
                    disabled={!selectedFile || loading}
                    className="bg-indigo-600 hover:bg-indigo-700 text-xs flex-1"
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Process Scanned Document
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Individual Stage Buttons */}
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'ocr', label: 'OCR', icon: FileText },
            { key: 'table-extract', label: 'Tables', icon: Table2 },
            { key: 'signature-detect', label: 'Signatures', icon: PenTool },
            { key: 'classify', label: 'Classify', icon: Shield },
          ].map(({ key, label, icon: Icon }) => (
            <Button
              key={key}
              data-testid={`vision-${key}-btn`}
              variant="outline"
              size="sm"
              disabled={!selectedFile || loading}
              onClick={() => runSingleStage(key, label)}
              className="text-xs"
            >
              <Icon className="h-3 w-3 mr-1" />
              {activeStage === label ? <Loader2 className="h-3 w-3 animate-spin" /> : label}
            </Button>
          ))}
        </div>

        {/* Reconstruct & Export */}
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
          <span className="text-xs font-semibold text-slate-600 mr-1">Reconstruct as:</span>
          <select
            value={reconstructFormat}
            onChange={(e) => setReconstructFormat(e.target.value)}
            disabled={loading}
            data-testid="vision-reconstruct-format"
            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
          >
            <option value="json">JSON</option>
            <option value="markdown">Markdown</option>
            <option value="html">HTML</option>
            <option value="csv">CSV</option>
            <option value="pdf">PDF</option>
            <option value="docx">DOCX</option>
          </select>
          <Button
            data-testid="vision-reconstruct-btn"
            variant="outline"
            size="sm"
            disabled={!selectedFile || loading}
            onClick={reconstructAndExport}
            className="text-xs"
          >
            {activeStage === 'Reconstruction' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
            Reconstruct & Export
          </Button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center gap-3 py-3">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
            <span className="text-sm text-slate-600">{activeStage || 'Processing...'}</span>
          </div>
        )}

        {/* Results Display */}
        {results && !loading && (
          <Tabs defaultValue="ocr" className="w-full">
            <TabsList className="grid w-full grid-cols-5 text-xs">
              <TabsTrigger value="ocr" className="text-xs">OCR</TabsTrigger>
              <TabsTrigger value="tables" className="text-xs">Tables</TabsTrigger>
              <TabsTrigger value="signatures" className="text-xs">Signatures</TabsTrigger>
              <TabsTrigger value="classify" className="text-xs">Classification</TabsTrigger>
              <TabsTrigger value="summary" className="text-xs">Summary</TabsTrigger>
            </TabsList>

            {/* OCR Tab */}
            <TabsContent value="ocr" className="mt-3">
              {results.ocr?.success ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-slate-500">Confidence:</span>
                    <ConfidenceBadge score={results.ocr?.ocr_metadata?.overall_confidence} />
                    <span className="text-slate-500">Quality:</span>
                    <Badge variant="outline" className="text-xs">{results.ocr?.document_info?.text_quality || 'N/A'}</Badge>
                    {results.ocr?.document_info?.is_handwritten && (
                      <Badge className="bg-amber-100 text-amber-700 text-xs">Handwritten</Badge>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 max-h-60 overflow-y-auto text-sm font-mono whitespace-pre-wrap">
                    {results.ocr?.full_text || 'No text extracted'}
                  </div>
                  {results.ocr?.text_blocks?.length > 0 && (
                    <div className="text-xs text-slate-500">
                      {results.ocr.text_blocks.length} text blocks detected |
                      Language: {results.ocr?.document_info?.primary_language || 'unknown'} |
                      Words: ~{results.ocr?.document_info?.estimated_word_count || 0}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No OCR results. Run OCR or Full Analysis first.</p>
              )}
            </TabsContent>

            {/* Tables Tab */}
            <TabsContent value="tables" className="mt-3">
              {results.tables?.tables?.length > 0 ? (
                <div className="space-y-3">
                  {results.tables.tables.map((table, idx) => (
                    <div key={idx} className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-3 py-2 flex items-center justify-between">
                        <span className="text-sm font-medium">{table.title || `Table ${idx + 1}`}</span>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs">{table.table_type || 'data'}</Badge>
                          <ConfidenceBadge score={table.confidence} />
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-100">
                              {(table.headers || []).map((h, hi) => (
                                <th key={hi} className="px-3 py-1.5 text-left font-medium">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(table.rows || []).slice(0, 10).map((row, ri) => (
                              <tr key={ri} className="border-t">
                                {row.map((cell, ci) => (
                                  <td key={ci} className="px-3 py-1.5">{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="bg-slate-50 px-3 py-1 text-xs text-slate-500">
                        {table.row_count} rows x {table.column_count} columns
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No tables detected. Run Table Extraction first.</p>
              )}
            </TabsContent>

            {/* Signatures Tab */}
            <TabsContent value="signatures" className="mt-3">
              {results.signatures?.detections?.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex gap-3 text-sm">
                    <Badge variant="outline">{results.signatures.summary?.total_signatures || 0} Signatures</Badge>
                    <Badge variant="outline">{results.signatures.summary?.total_stamps || 0} Stamps</Badge>
                    <Badge variant="outline">{results.signatures.summary?.total_seals || 0} Seals</Badge>
                  </div>
                  {results.signatures.detections.map((d, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium capitalize">{d.type?.replace('_', ' ')}</span>
                        <p className="text-xs text-slate-500">{d.description}</p>
                        {d.associated_text && <p className="text-xs text-slate-400 mt-0.5">Near: "{d.associated_text}"</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <ConfidenceBadge score={d.confidence} />
                        {d.appears_authentic && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No signatures/stamps detected.</p>
              )}
            </TabsContent>

            {/* Classification Tab */}
            <TabsContent value="classify" className="mt-3">
              {results.classification?.primary_type ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-indigo-100 text-indigo-700 text-sm">
                      {results.classification.primary_type_label || results.classification.primary_type}
                    </Badge>
                    <ConfidenceBadge score={results.classification.confidence} />
                    {results.classification.industry && (
                      <Badge variant="outline" className="text-xs capitalize">{results.classification.industry}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">{results.classification.classification_reasoning}</p>
                  {results.classification.detected_features?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {results.classification.detected_features.map((f, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No classification. Run Classify first.</p>
              )}
            </TabsContent>

            {/* Summary Tab */}
            <TabsContent value="summary" className="mt-3">
              <div className="space-y-3">
                {results.stages && (
                  <div className="text-sm">
                    <span className="font-medium">Pipeline stages: </span>
                    {results.stages.map((s, i) => (
                      <Badge key={i} variant="outline" className="text-xs mr-1 mb-1">{s}</Badge>
                    ))}
                  </div>
                )}
                {results.correction?.total_corrections > 0 && (
                  <div className="p-3 bg-amber-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2 font-medium text-amber-700">
                      <RefreshCw className="h-4 w-4" />
                      {results.correction.total_corrections} OCR corrections applied
                    </div>
                  </div>
                )}
                {results.normalization?.success && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2 font-medium text-blue-700">
                      <Languages className="h-4 w-4" />
                      Multilingual normalization: {results.normalization.normalization_stats?.languages_found?.join(', ') || 'Done'}
                    </div>
                  </div>
                )}
                {results.reconstruction?.success && (
                  <div className="p-3 bg-emerald-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2 font-medium text-emerald-700">
                      <Download className="h-4 w-4" />
                      Document reconstructed ({results.reconstruction.format || 'json'})
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
      {dialog}
    </Card>
  );
};

export default VisionInsightsPanel;
